/**
 * CANONICAL provider classification for a model id.
 *
 * sphinx serves exactly two providers. A model id is either one of the four
 * catalog ids (possibly spelled with an `anthropic/` or `openai/` prefix), or
 * it is unknown. PURE: no catalog lookup beyond SUPPORTED_MODELS, no network.
 */

import { getModelById, isModelProvider, type ModelProvider } from "./types";

export type ModelProviderClassification = {
  provider: ModelProvider;
};

/**
 * Classify a model id:
 *
 *   1. Trim. An empty or whitespace-only id has no provider — `null`.
 *   2. A recognized `<provider>/` prefix (`anthropic/…`, `openai/…`).
 *   3. A bare catalog id → that row's provider.
 *   4. Everything else → `null`. No guessing: an id that is not one of ours
 *      has no provider that could serve it.
 */
export function classifyModelIdProvider(
  modelId: string
): ModelProviderClassification | null {
  const id = modelId.trim();
  if (id.length === 0) return null;

  const slashIdx = id.indexOf("/");
  if (slashIdx > 0) {
    const prefix = id.slice(0, slashIdx);
    if (isModelProvider(prefix)) return { provider: prefix };
  }

  const catalogHit = getModelById(id);
  return catalogHit ? { provider: catalogHit.provider } : null;
}

/**
 * {@link classifyModelIdProvider} reduced to the provider name. `null` for a
 * blank or unknown id, same contract.
 */
export function providerForModelId(modelId: string): ModelProvider | null {
  return classifyModelIdProvider(modelId)?.provider ?? null;
}
